import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

// ── Interfaces ──────────────────────────────────────────────────────────

interface MdtModuleEntryData {
  callSign: string | null;
  companyVehicleId: number | null;
  availableCompanyVehicles: Array<{ id: number; vehicleName: string }>;
  availableCallSigns: Array<{ id: number; callSign: string }> | null;
}

interface MdtModuleEntryCallbacks {
  onConfirm: (callSign: string | null, companyVehicleId: number | null) => void;
}

export interface MdtModuleEntryModalProps {
  open: boolean;
  callSign?: string | null;
  companyVehicleId?: number | null;
  availableCompanyVehicles?: Array<{ id: number; vehicleName: string }>;
  availableCallSigns?: Array<{ id: number; callSign: string }> | null;
  scrollLock?: boolean;
  theme?: string;
  /** Increment this counter to force-reset the saving state (used by the imperative wrapper on API error). */
  resetSavingKey?: number;
  onConfirm: (result: { callSign: string | null; companyVehicleId: number | null }) => void;
}

// ── Component ───────────────────────────────────────────────────────────

export function MdtModuleEntryModal({
  open,
  callSign = null,
  companyVehicleId = null,
  availableCompanyVehicles = [],
  availableCallSigns = null,
  scrollLock = true,
  theme: themeProp,
  resetSavingKey = 0,
  onConfirm,
}: MdtModuleEntryModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [localCallSign, setLocalCallSign] = useState<string | null>(callSign);
  const [localCompanyVehicleId, setLocalCompanyVehicleId] = useState<number | null>(companyVehicleId);
  const [isSaving, setIsSaving] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Sync props to local state when they change externally
  useEffect(() => {
    setLocalCallSign(callSign);
  }, [callSign]);

  useEffect(() => {
    setLocalCompanyVehicleId(companyVehicleId);
  }, [companyVehicleId]);

  // Reset local state when modal opens
  useEffect(() => {
    if (open) {
      setLocalCallSign(callSign);
      setLocalCompanyVehicleId(companyVehicleId);
      setIsSaving(false);

      // Focus first input
      requestAnimationFrame(() => {
        const firstInput = dialogRef.current?.querySelector(
          'input, select, .kst-select__control'
        ) as HTMLElement | null;
        firstInput?.focus();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset saving state when resetSavingKey changes (used by wrapper on API error)
  useEffect(() => {
    if (resetSavingKey > 0) {
      setIsSaving(false);
    }
  }, [resetSavingKey]);

  // Scroll lock
  useEffect(() => {
    if (scrollLock && open) {
      document.body.classList.add('kstm-scroll-lock');
    }
    return () => {
      if (scrollLock) {
        document.body.classList.remove('kstm-scroll-lock');
      }
    };
  }, [open, scrollLock]);

  // ── Computed values ───────────────────────────────────────────────────

  const hasCallSignList = availableCallSigns !== null && availableCallSigns.length > 0;

  const isCurrentCallSignInvalid = useMemo(() => {
    if (!hasCallSignList) return false;
    if (!localCallSign) return false;
    return !availableCallSigns!.some((cs) => cs.callSign === localCallSign);
  }, [hasCallSignList, localCallSign, availableCallSigns]);

  const isCallSignValid = useMemo(() => {
    if (!hasCallSignList) return true; // Text input mode - any value is valid
    if (!localCallSign) return true; // Empty is valid
    return availableCallSigns!.some((cs) => cs.callSign === localCallSign);
  }, [hasCallSignList, localCallSign, availableCallSigns]);

  const canConfirm = !isSaving && isCallSignValid;

  const showCallSignWarning = hasCallSignList && isCurrentCallSignInvalid;

  const vehicleItems = useMemo(
    () =>
      (availableCompanyVehicles || []).map((v) => ({
        value: v.id,
        label: v.vehicleName,
      })),
    [availableCompanyVehicles]
  );

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    setIsSaving(true);
    onConfirm({
      callSign: localCallSign,
      companyVehicleId: localCompanyVehicleId,
    });
  }, [canConfirm, localCallSign, localCompanyVehicleId, onConfirm]);

  const onCallSignInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalCallSign(e.target.value || null);
  }, []);

  const onCallSignSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setLocalCallSign(value === '' ? null : value);
  }, []);

  const onVehicleChange = useCallback((item: { value: number; label: string } | null) => {
    setLocalCompanyVehicleId(item ? item.value : null);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────

  if (!open) return null;

  return (
    <div data-kstm-modal className={`kstm-backdrop ${theme}`}>
      <div className="kstm-dialog" role="dialog" aria-modal="true" ref={dialogRef}>
        <div className="kstm-header">
          <h2 className="kstm-title">
            <i className="fa fa-shield" style={{ marginRight: 8 }} />
            Mobile Module Entry
          </h2>
        </div>

        <div className="kstm-body">
          <p className="kstm-description">
            Please confirm or update your call sign and assigned vehicle before entering the Mobile
            module.
          </p>

          {/* Call Sign Warning */}
          {showCallSignWarning && (
            <div className="kstm-warning">
              <i className="fa fa-exclamation-triangle" />
              Your current call sign is no longer available. Please select a new call sign to
              continue.
            </div>
          )}

          {/* Call Sign Field */}
          <div className="kstm-field">
            <label className="kstm-label">Call Sign</label>

            {!hasCallSignList ? (
              /* Text input when no call sign list */
              <input
                type="text"
                className="kstm-input"
                value={localCallSign ?? ''}
                onChange={onCallSignInputChange}
                disabled={isSaving}
                placeholder="Enter call sign"
              />
            ) : (
              /* Dropdown when call sign list exists */
              <>
                <select
                  className={`kstm-select${!isCallSignValid ? ' kstm-select-invalid' : ''}`}
                  value={localCallSign ?? ''}
                  onChange={onCallSignSelectChange}
                  disabled={isSaving}
                >
                  <option value="">-- Select Call Sign --</option>
                  {/* Show current invalid call sign as disabled option */}
                  {isCurrentCallSignInvalid && (
                    <option value={localCallSign ?? ''} disabled className="kstm-option-invalid">
                      {localCallSign} (no longer available)
                    </option>
                  )}
                  {availableCallSigns!.map((cs) => (
                    <option key={cs.id} value={cs.callSign}>
                      {cs.callSign}
                    </option>
                  ))}
                </select>
                {!isCallSignValid && (
                  <small className="kstm-error-text">
                    Please select a valid call sign from the list.
                  </small>
                )}
              </>
            )}
          </div>

          {/* Vehicle Field - Using Dropdownlist */}
          <div className="kstm-field kstm-field--vehicle">
            <label className="kstm-label">Assigned Vehicle</label>
            <div className="kstm-vehicle-select-wrapper">
              <Dropdownlist
                value={localCompanyVehicleId}
                items={vehicleItems}
                labelKey="label"
                valueKey="value"
                placeholder="-- Select Vehicle --"
                searchable={vehicleItems.length > 5}
                onItemChange={onVehicleChange}
              />
            </div>
          </div>
        </div>

        <div className="kstm-actions">
          <button
            className={`kstm-btn kstm-btn--primary${!canConfirm ? ' kstm-btn--disabled' : ''}`}
            disabled={!canConfirm}
            onClick={handleConfirm}
            type="button"
          >
            {isSaving ? (
              <span>
                <i className="fa fa-spinner fa-spin" /> Saving...
              </span>
            ) : (
              <span>
                <i className="fa fa-check" /> Confirm &amp; Enter Mobile
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Imperative helpers ──────────────────────────────────────────────────

let mdtModuleEntryContainer: HTMLDivElement | null = null;

/**
 * Wrapper component that manages modal state and the confirm/error flow.
 * Rendered imperatively via renderReactComponent.
 */
function MdtModuleEntryModalWrapper({
  data,
  callbacks,
}: {
  data: MdtModuleEntryData;
  callbacks: MdtModuleEntryCallbacks;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [resetSavingKey, setResetSavingKey] = useState(0);

  const handleConfirm = useCallback(
    (result: { callSign: string | null; companyVehicleId: number | null }) => {
      const originalCallSign = data.callSign;
      const originalCompanyVehicleId = data.companyVehicleId;
      const hasChanges =
        result.callSign !== originalCallSign ||
        result.companyVehicleId !== originalCompanyVehicleId;

      const finishEntry = () => {
        App.hasMdtModuleEntryBeenConfirmed = true;
        // Close and destroy
        setIsOpen(false);
        setTimeout(() => {
          hideMdtModuleEntryModal();
        }, 150);
        callbacks.onConfirm(result.callSign, result.companyVehicleId);
      };

      if (hasChanges) {
        api.updateOfficerModuleEntry(
          { callSign: result.callSign, companyVehicleId: result.companyVehicleId },
          () => {
            finishEntry();
          },
          () => {
            App.showPopupNotification('Failed to save changes. Please try again.', 'error');
            // Re-enable the confirm button by bumping the reset key
            setResetSavingKey((k) => k + 1);
          }
        );
      } else {
        finishEntry();
      }
    },
    [data, callbacks]
  );

  return (
    <MdtModuleEntryModal
      open={isOpen}
      callSign={data.callSign}
      companyVehicleId={data.companyVehicleId}
      availableCompanyVehicles={data.availableCompanyVehicles || []}
      availableCallSigns={data.availableCallSigns}
      resetSavingKey={resetSavingKey}
      onConfirm={handleConfirm}
    />
  );
}

/**
 * Imperatively show the MDT Module Entry modal.
 * Creates a temporary container, mounts the React wrapper via the bridge.
 */
export function showMdtModuleEntryModal(
  data: MdtModuleEntryData,
  callbacks: MdtModuleEntryCallbacks
) {
  // Destroy existing instance if present
  hideMdtModuleEntryModal();

  // Create mount point
  const container = document.createElement('div');
  container.id = 'mdtModuleEntryModalMount';
  document.body.appendChild(container);
  mdtModuleEntryContainer = container;

  renderReactComponent(
    MdtModuleEntryModalWrapper,
    { data, callbacks },
    container
  );
}

/**
 * Imperatively hide and destroy the MDT Module Entry modal.
 */
export function hideMdtModuleEntryModal() {
  if (mdtModuleEntryContainer) {
    destroyReactComponent(mdtModuleEntryContainer);
    if (mdtModuleEntryContainer.parentNode) {
      mdtModuleEntryContainer.parentNode.removeChild(mdtModuleEntryContainer);
    }
    mdtModuleEntryContainer = null;
  }
}

/**
 * Handler for MDT module selection - call from module selection flow.
 * Shows the modal if the MDT module is selected, feature is enabled,
 * and the user hasn't already confirmed this session.
 */
export function handleMdtModuleSelection(result: any, proceedCallback: () => void) {
  const shouldShowModal =
    result.ModuleType === 'mdt' &&
    result.isMdtEntryPromptEnabled === true &&
    !App.hasMdtModuleEntryBeenConfirmed;

  if (shouldShowModal) {
    showMdtModuleEntryModal(
      {
        callSign: result.callSign,
        companyVehicleId: result.companyVehicleId,
        availableCompanyVehicles: result.availableCompanyVehicles || [],
        availableCallSigns: result.availableCallSigns,
      },
      {
        onConfirm: (_newCallSign: string | null, _newVehicleId: number | null) => {
          proceedCallback();
        },
      }
    );
  } else {
    // Not MDT, feature not enabled, or already confirmed this session
    proceedCallback();
  }
}

// ── Window registrations ────────────────────────────────────────────────

(window as any).MdtModuleEntryModalReact = MdtModuleEntryModal;
(window as any).showMdtModuleEntryModal = showMdtModuleEntryModal;
(window as any).hideMdtModuleEntryModal = hideMdtModuleEntryModal;
(window as any).handleMdtModuleSelection = handleMdtModuleSelection;
